interface SunCompassProps {
  altitude: number;
  azimuth: number;
  size?: number;
}

const DIRECTIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compassDirection = (azimuth: number) => DIRECTIONS[Math.round(azimuth / 45) % 8];

// Centre = straight overhead, rim = horizon; below-horizon sun is pinned to the rim.
function skyPoint(altitude: number, azimuth: number, c: number, R: number) {
  const r = altitude > 0 ? ((90 - altitude) / 90) * R : R;
  const a = (azimuth * Math.PI) / 180;
  return { x: c + r * Math.sin(a), y: c - r * Math.cos(a) };
}

function CardinalLabels({ c, size }: { c: number; size: number }) {
  const labels: [string, number, number][] = [
    ["N", c, 10],
    ["S", c, size - 3],
    ["E", size - 6, c + 3],
    ["W", 6, c + 3],
  ];
  return (
    <>
      {labels.map(([l, x, y]) => (
        <text key={l} x={x} y={y} textAnchor="middle" fontSize="10" fontFamily="Inter, sans-serif" fill="#8a6f52">
          {l}
        </text>
      ))}
    </>
  );
}

function CompassDial({ altitude, azimuth, size }: Required<SunCompassProps>) {
  const c = size / 2;
  const R = c - 16;
  const up = altitude > 0;
  const sun = skyPoint(altitude, azimuth, c, R);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Where the sun is in the sky now">
      <circle cx={c} cy={c} r={R} fill={up ? "#fbf6ea" : "#2b1f14"} fillOpacity={up ? 1 : 0.08} stroke="#d9c9ad" />
      <circle cx={c} cy={c} r={(R * 2) / 3} fill="none" stroke="#d9c9ad" strokeDasharray="2 4" />
      <circle cx={c} cy={c} r={R / 3} fill="none" stroke="#d9c9ad" strokeDasharray="2 4" />
      <line x1={c} y1={c - R} x2={c} y2={c + R} stroke="#e6dac4" />
      <line x1={c - R} y1={c} x2={c + R} y2={c} stroke="#e6dac4" />
      <CardinalLabels c={c} size={size} />
      <line x1={c} y1={c} x2={sun.x} y2={sun.y} stroke="#c0501b" strokeOpacity={up ? 0.5 : 0.25} strokeDasharray="2 3" />
      <circle cx={sun.x} cy={sun.y} r={up ? 12 : 9} fill="#c0501b" opacity={0.15} />
      <circle cx={sun.x} cy={sun.y} r={up ? 6 : 4.5} fill={up ? "#c0501b" : "#f6efe3"} stroke="#c0501b" strokeWidth="1.5" />
    </svg>
  );
}

function CompassReadout({ altitude, azimuth }: { altitude: number; azimuth: number }) {
  const up = altitude > 0;
  return (
    <dl className="font-sans text-xs leading-relaxed text-[#8a6f52]">
      <dt className="uppercase tracking-[0.3em]">Sun now</dt>
      <dd data-testid="solar-sun-altitude" className="mt-1 font-serif text-lg tabular-nums text-[#2b1f14]">
        {altitude >= 0 ? "+" : "−"}
        {Math.abs(altitude).toFixed(1)}° {up ? "high" : "below"}
      </dd>
      <dd data-testid="solar-sun-azimuth" className="font-serif text-lg tabular-nums text-[#2b1f14]">
        {azimuth.toFixed(0)}° {compassDirection(azimuth)}
      </dd>
      <dd className="italic">{up ? "above the horizon" : "under the horizon"}</dd>
    </dl>
  );
}

export default function SunCompass({ altitude, azimuth, size = 150 }: SunCompassProps) {
  return (
    <div data-testid="solar-sun-compass" className="flex items-center gap-5">
      <CompassDial altitude={altitude} azimuth={azimuth} size={size} />
      <CompassReadout altitude={altitude} azimuth={azimuth} />
    </div>
  );
}
